import React, { useState, useMemo, useCallback, useEffect } from "react";
import { InputAmount } from "@kelp-dao/kernel-design-system";
import { CoinOption } from "@kelp-dao/kernel-design-system/dist/components/text-fields/InputAmount";
import BaseImage from "@components/BaseImage";
import {
  toFixedWithoutRounding,
  MAX_FRACTION_DIGITS,
  isNumber,
  getLSTOptionsForChain,
  LSTIconMapping,
  LstOptions,
  CONNECT_WALLET_CTA,
  SWITCH_NETWORK,
  DEPOSIT_NOW,
  APPROVE_CTA,
  WITHDRAW_CTA,
} from "@constants/common";
import { formatNumber, getUrlForLocalImage, trackEvent } from "utils";
import { useSelector, useDispatch } from "react-redux";
import { updateUserData } from "@store";
import useDappChain from "@hooks/useDappChain";
import useUserType from "@hooks/useUserType";
import useDappConfig from "@hooks/useDappConfig";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { emitReduxEvent } from "utils";
import { EVENTS } from "@constants/reduxEvents";
import {
  VAULT_CONNECT_WALLET_CLICK,
  VAULT_APPROVE_CLICK,
  VAULT_DEPOSIT_CTA,
  VAULT_WITHDRAW_CTA,
  VAULT_MAX_DEPOSIT_CLICK,
} from "@constants/analytics";
import { useEstimatedGasFee } from "@hooks/useGasFeeEstimation";
import { ETH } from "@config/contractsConfig";

export interface KernelVaultInputProps {
  type: "deposit" | "withdraw";

  // Input token (what user provides)
  inputTokenSymbol: string;
  inputTokenBalance: number;
  inputTokenIcon?: string;

  // Output token (what user receives)
  outputTokenSymbol: string;
  outputTokenBalance: number;
  outputTokenIcon?: string;

  // Converted amount (calculated by vault hook based on current input amount)
  convertedAmount: string;
  exchangeRateDisplay?: string;

  // State
  isConnected: boolean;
  isApproved?: boolean; // for deposits
  isSupportedChain?: boolean;
  disabled?: boolean;

  // Callbacks
  onMaxClick?: () => void;
  onAmountChange?: (amount: string) => void;
  onTokenSelect?: (tokenSymbol: string) => void;
  onOutputTokenSelect?: (tokenSymbol: string) => void;
  onApprove?: (amount?: string) => void; // for deposits
  onDeposit?: (amount?: string) => void; // for deposits
  onWithdraw?: (amount?: string) => void; // for withdraws

  // Validation
  checkInputConstraints?: (amount: string) => string;

  // Optional
  className?: string;
  placeholder?: string;
  disclaimer?: React.ReactNode;
  enableTokenSelection?: boolean;
  enableOutputTokenSelection?: boolean;
  vaultType?: "airdrop" | "highGrowth" | "stable" | "yield" | "cryptanium";
}

const KernelVaultInput: React.FC<KernelVaultInputProps> = ({
  type,
  inputTokenSymbol,
  inputTokenBalance,
  inputTokenIcon,
  outputTokenSymbol,
  outputTokenBalance,
  outputTokenIcon,
  convertedAmount,
  exchangeRateDisplay,
  isConnected,
  isApproved = false,
  isSupportedChain = true,
  disabled = false,
  onMaxClick,
  onAmountChange,
  onTokenSelect,
  onOutputTokenSelect,
  onApprove,
  onDeposit,
  onWithdraw,
  checkInputConstraints,
  className = "",
  placeholder = "0",
  disclaimer,
  enableTokenSelection = true,
  enableOutputTokenSelection = false,
  vaultType,
}) => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const { openConnectModal } = useConnectModal();

  // Reset amount and error when type changes (tab change)
  useEffect(() => {
    setAmount("");
    setError("");
    onAmountChange?.("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Redux and hooks for token selection
  const {
    selectedERC20Vault,
    selectedERC20YieldVault,
    selectedERC20StableVault,
    selectedERC20Cryptanium,
    selectedOutputTokenStableVault,
    selectedOutputTokenCryptaniumVault,
  } = useSelector((state: any) => state.user);
  const dispatch = useDispatch();
  const { chain } = useDappChain();
  const { isStaderLedgerUser } = useUserType();
  const { stableVaultConfig, cryptaniumVaultConfig } = useDappConfig();

  const { estimatedGasFee } = useEstimatedGasFee();

  // Input validation
  const checkInput = useCallback(
    (value = amount.trim()) => {
      if (!isConnected) return "";

      // Use custom constraint checker if provided
      if (checkInputConstraints) {
        return checkInputConstraints(value);
      }

      // Default validation
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > inputTokenBalance) {
        error = `You can't ${type} more than available balance in your wallet.`;
      }
      return error;
    },
    [isConnected, inputTokenBalance, amount, type, checkInputConstraints]
  );

  useEffect(() => {
    if (amount !== "") {
      const error = checkInput(amount);
      setError(error);
    }
  }, [inputTokenBalance, amount, checkInput]);

  // Get selected asset from Redux
  const getSelectedAsset = () => {
    if (!enableTokenSelection) return inputTokenSymbol;

    switch (vaultType) {
      case "yield":
        return selectedERC20YieldVault;
      case "stable":
        return selectedERC20StableVault;
      case "cryptanium":
        return selectedERC20Cryptanium;
      default:
        return selectedERC20Vault;
    }
  };

  const selectedAsset = getSelectedAsset();

  // Get selected output asset from Redux
  const getSelectedOutputAsset = () => {
    if (!enableOutputTokenSelection || type !== "withdraw")
      return outputTokenSymbol;

    switch (vaultType) {
      case "stable":
        return selectedOutputTokenStableVault || outputTokenSymbol;
      case "cryptanium":
        return selectedOutputTokenCryptaniumVault || outputTokenSymbol;
      default:
        return outputTokenSymbol;
    }
  };

  const selectedOutputAsset = getSelectedOutputAsset();

  // Handle input change
  const handleChange = useCallback(
    (value: string) => {
      const val = value.trim().replace(/,/g, "");
      if (isNumber(val) || val === "") {
        if (val.indexOf(".") === -1) {
          setAmount(val);
          onAmountChange?.(val);
        } else {
          if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
            const newAmount = val === "." ? "0." : val;
            setAmount(newAmount);
            onAmountChange?.(newAmount);
          }
        }
      }
    },
    [onAmountChange]
  );

  // Handle max click
  const handleMaxClick = useCallback(() => {
    trackEvent(VAULT_MAX_DEPOSIT_CLICK);
    if (onMaxClick) {
      onMaxClick();
    } else {
      if (selectedAsset === ETH) {
        const amount = Math.max(
          0,
          inputTokenBalance - (Number(estimatedGasFee) || 0)
        ).toString();
        handleChange(toFixedWithoutRounding(amount.toString(), 4));
      } else {
        handleChange(toFixedWithoutRounding(inputTokenBalance.toString(), 4));
      }
      // handleChange(toFixedWithoutRounding(inputTokenBalance.toString(), 4));
    }
  }, [
    handleChange,
    inputTokenBalance,
    onMaxClick,
    estimatedGasFee,
    selectedAsset,
  ]);

  // Action button logic owned here so we can check local amount and error
  const handleActionClick = useCallback(() => {
    if (!isConnected) {
      // Track connect wallet click
      trackEvent(VAULT_CONNECT_WALLET_CLICK, {
        cta_location: `vault_${type}_widget`,
        cta: CONNECT_WALLET_CTA,
        vault_type: vaultType,
      });
      openConnectModal?.();
      return;
    }

    if (!isSupportedChain) {
      emitReduxEvent(EVENTS.SWITCH_NETWORK);
      return;
    }

    if (!amount || error) {
      return;
    }

    // Handle approval for both deposit and withdraw
    if (!isApproved && onApprove) {
      // Track approve click
      trackEvent(VAULT_APPROVE_CLICK, {
        vault_type: vaultType,
        action_type: type,
        amount: amount,
        token: selectedAsset,
      });
      onApprove(amount);
      return;
    }

    // Track deposit/withdraw CTA click
    if (type === "deposit") {
      trackEvent(VAULT_DEPOSIT_CTA, {
        vault_type: vaultType,
        amount: amount,
        token: selectedAsset,
        output_token: selectedOutputAsset,
      });
      onDeposit?.(amount);
    } else {
      trackEvent(VAULT_WITHDRAW_CTA, {
        vault_type: vaultType,
        amount: amount,
        token: selectedAsset,
        output_token: selectedOutputAsset,
      });
      onWithdraw?.(amount);
    }

    setAmount("");
    setError("");
  }, [
    isConnected,
    isSupportedChain,
    amount,
    error,
    type,
    isApproved,
    onApprove,
    onDeposit,
    onWithdraw,
    openConnectModal,
    vaultType,
    selectedAsset,
    selectedOutputAsset,
  ]);

  const getActionButtonText = useCallback(() => {
    if (!isConnected) return CONNECT_WALLET_CTA;
    if (!isSupportedChain) return SWITCH_NETWORK;

    // Show approve button if not approved (for both deposit and withdraw)
    if (!isApproved) return APPROVE_CTA;

    // Show action button based on type
    if (type === "deposit") return DEPOSIT_NOW;
    return WITHDRAW_CTA;
  }, [isConnected, isSupportedChain, type, isApproved]);

  // Get available token options based on vault type
  const getTokenOptions = useMemo(() => {
    if (!enableTokenSelection || !isSupportedChain) {
      return [
        {
          key: inputTokenSymbol,
          title: inputTokenSymbol,
          priority: 0,
        },
      ];
    }

    // Filter options based on vault type
    switch (vaultType) {
      case "stable": {
        // For stable vault, use only stable tokens from config
        if (stableVaultConfig?.tokens) {
          return Object.keys(stableVaultConfig.tokens).map((tokenKey) => {
            const token = stableVaultConfig.tokens[tokenKey];
            return {
              key: token.symbol,
              title: token.symbol,
              priority: token.priority || 0,
            };
          });
        }
        return [];
      }

      case "cryptanium": {
        // For cryptanium vault, use only cryptanium tokens from config
        if (cryptaniumVaultConfig?.tokens) {
          return Object.keys(cryptaniumVaultConfig.tokens).map((tokenKey) => {
            const token = cryptaniumVaultConfig.tokens[tokenKey];
            return {
              key: token.symbol,
              title: token.symbol,
              priority: token.priority || 0,
            };
          });
        }
        return [];
      }

      case "yield":
      case "highGrowth":
      case "airdrop":
      default: {
        // For LST-based vaults, use LST options but exclude stable tokens
        const options = getLSTOptionsForChain(
          chain.id,
          false, // Don't hide ETH
          isStaderLedgerUser,
          true // isVaultPage
        );

        // Filter out stable tokens and dividers
        const stableTokens = ["USDT", "USDC", "sbUSD", "xgUSD"];
        return options
          .filter(
            (option) =>
              option.key !== "divider" &&
              option.key !== "other" &&
              !stableTokens.includes(option.key)
          )
          .sort((a: any, b: any) => (a?.priority || 0) - (b?.priority || 0));
      }
    }
  }, [
    chain.id,
    isStaderLedgerUser,
    enableTokenSelection,
    inputTokenSymbol,
    vaultType,
    stableVaultConfig,
    cryptaniumVaultConfig,
  ]);

  // Get available output token options for withdraw operations
  const getOutputTokenOptions = useMemo(() => {
    if (!enableOutputTokenSelection || type !== "withdraw") {
      return [
        {
          key: outputTokenSymbol,
          title: outputTokenSymbol,
          priority: 0,
        },
      ];
    }

    // Filter output options based on vault type
    switch (vaultType) {
      case "stable": {
        // For stable vault withdraw, user can choose USDT or USDC
        if (stableVaultConfig?.tokens) {
          return Object.keys(stableVaultConfig.tokens).map((tokenKey) => {
            const token = stableVaultConfig.tokens[tokenKey];
            return {
              key: token.symbol,
              title: token.symbol,
              priority: token.priority || 0,
            };
          });
        }
        return [];
      }

      case "cryptanium": {
        // For cryptanium vault withdraw, user can choose supported tokens
        if (cryptaniumVaultConfig?.tokens) {
          return Object.keys(cryptaniumVaultConfig.tokens).map((tokenKey) => {
            const token = cryptaniumVaultConfig.tokens[tokenKey];
            return {
              key: token.symbol,
              title: token.symbol,
              priority: token.priority || 0,
            };
          });
        }
        return [];
      }

      default: {
        // For other vaults, typically single output token
        return [
          {
            key: outputTokenSymbol,
            title: outputTokenSymbol,
            priority: 0,
          },
        ];
      }
    }
  }, [
    enableOutputTokenSelection,
    type,
    outputTokenSymbol,
    vaultType,
    stableVaultConfig,
    cryptaniumVaultConfig,
  ]);

  // Create coin options for input
  const inputCoinOptions: CoinOption[] = getTokenOptions.map(
    (option: LstOptions) => ({
      id: option.key,
      label: option.title || option.key,
      iconSrc:
        getUrlForLocalImage(LSTIconMapping[option.key]) || inputTokenIcon || "",
      isDisabled: option.isDisabled || false,
    })
  );

  // Create coin options for output
  const outputCoinOptions: CoinOption[] = getOutputTokenOptions.map(
    (option: LstOptions) => ({
      id: option.key,
      label: option.title || option.key,
      iconSrc:
        getUrlForLocalImage(LSTIconMapping[option.key]) ||
        outputTokenIcon ||
        "",
      isDisabled: option.isDisabled || false,
    })
  );

  const selectedInputCoin =
    inputCoinOptions.find((coin) => coin.id === selectedAsset) ||
    inputCoinOptions[0];
  const selectedOutputCoin =
    outputCoinOptions.find((coin) => coin.id === selectedOutputAsset) ||
    outputCoinOptions[0];

  // Handle token selection
  const handleTokenSelect = (coin: CoinOption) => {
    if (!enableTokenSelection) return;

    // Update Redux store based on vault type
    const getReduxKey = () => {
      switch (vaultType) {
        case "yield":
          return "selectedERC20YieldVault";
        case "stable":
          return "selectedERC20StableVault";
        case "cryptanium":
          return "selectedERC20Cryptanium";
        default:
          return "selectedERC20Vault";
      }
    };

    dispatch(updateUserData({ [getReduxKey()]: coin.id }));

    // Call external callback if provided
    onTokenSelect?.(coin.id);
  };

  // Handle output token selection
  const handleOutputTokenSelect = (coin: CoinOption) => {
    if (!enableOutputTokenSelection || type !== "withdraw") return;

    // Update Redux store based on vault type
    const getOutputReduxKey = () => {
      switch (vaultType) {
        case "stable":
          return "selectedOutputTokenStableVault";
        case "cryptanium":
          return "selectedOutputTokenCryptaniumVault";
        default:
          return null;
      }
    };

    const reduxKey = getOutputReduxKey();
    if (reduxKey) {
      dispatch(updateUserData({ [reduxKey]: coin.id }));
    }

    // Call external callback if provided
    onOutputTokenSelect?.(coin.id);
  };

  const formatDisplayAmount = (value: string | number) => {
    return value && isNumber(value.toString())
      ? toFixedWithoutRounding(value.toString(), 4)
      : "0";
  };

  return (
    <div
      className={`w-full ${className}`}
      style={{ position: "relative", zIndex: 5 }}
    >
      <div
        className="text-sm leading-5 font-normal pt-5 flex flex-col"
        style={{ overflow: "visible" }}
      >
        <div
          className="flex justify-between mb-[2px] pt-2.5 tablet:pt-5 pb-2.5 tablet:pb-0 gap-5 tablet:gap-3 flex-col tablet:flex-row md:flex-wrap lg:flex-nowrap items-stretch md:items-end tablet:items-center lg:items-center"
          style={{ overflow: "visible" }}
        >
          {/* Input Section */}
          <div
            className="w-full tablet:w-[48%] lg:w-[38%] flex flex-col"
            style={{ overflow: "visible", position: "relative", zIndex: 20 }}
          >
            <span className="text-xs leading-[14.4px] font-normal mb-2">
              {type === "deposit" ? "You deposit" : "Withdraw from vault"}
            </span>

            <InputAmount
              value={amount}
              onChange={handleChange}
              placeholder={placeholder}
              error={!!error}
              disabled={disabled}
              coins={inputCoinOptions}
              coinSelected={selectedInputCoin}
              onCoinSelect={
                enableTokenSelection ? handleTokenSelect : undefined
              }
              rightButton={{
                show: true,
                text: "Max",
                disabled: !isConnected,
                onClick: handleMaxClick,
              }}
              className="w-full"
              showBalance={true}
              coinBalance={formatDisplayAmount(inputTokenBalance)}
              dropdownWidth={200}
              style={{ zIndex: 10 }}
            />

            {/* Error below input on mobile */}
            {error && (
              <div className="flex items-center mt-2 tablet:hidden">
                <BaseImage
                  src="/assets/transactions/error.svg"
                  width={20}
                  height={20}
                  alt="error-icon"
                />
                <span className="ml-2 text-error text-xs font-normal">
                  {error}
                </span>
              </div>
            )}
          </div>

          {/* Output Section */}
          <div className="w-full tablet:w-[48%] lg:w-[38%] flex flex-col">
            <span className="text-xs leading-[14.4px] font-normal mb-2">
              {type === "deposit" ? "You get" : "To your wallet"}
            </span>

            <InputAmount
              value={
                formatNumber(
                  Number(convertedAmount),
                  false,
                  MAX_FRACTION_DIGITS,
                  true
                ) || "0"
              }
              onChange={() => {}} // Read-only
              placeholder="0"
              error={false}
              disabled={true} // Only disable if dropdown is not enabled
              coins={outputCoinOptions}
              coinSelected={selectedOutputCoin}
              onCoinSelect={
                enableOutputTokenSelection ? handleOutputTokenSelect : undefined
              }
              rightButton={{
                show: false,
              }}
              className="w-full cursor-not-allowed"
              showBalance={true}
              coinBalance={formatDisplayAmount(outputTokenBalance)}
              dropdownWidth={200}
              inputStyle={{
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
                color: "#666",
              }}
            />
          </div>

          {/* Error below input on tablet */}
          {error && (
            <div className="hidden tablet:max-tablet:flex items-center mt-2">
              <BaseImage
                src="/assets/transactions/error.svg"
                width={20}
                height={20}
                alt="error-icon"
              />
              <span className="ml-2 text-error text-xs font-normal">
                {error}
              </span>
            </div>
          )}

          {/* Action Button Section */}
          <div className="w-full md:w-full lg:w-[30%] flex flex-col justify-center items-stretch lg:items-start mt-2 tablet:mt-[18px]">
            <button
              className="rounded-xl bg-button-primary-bg p-2 px-4 h-12 w-full hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleActionClick}
              disabled={
                isSupportedChain
                  ? !!disabled ||
                    (isConnected && (!amount || amount === "")) ||
                    !!error
                  : false
              }
            >
              <span className="text-base leading-6 font-semibold text-white">
                {getActionButtonText()}
              </span>
            </button>
          </div>
        </div>

        {/* Error Display (desktop) */}
        {error && (
          <div className="hidden md:hidden lg:flex xl:flex items-center mt-2 mx-4">
            <BaseImage
              src="/assets/transactions/error.svg"
              width={20}
              height={20}
              alt="error-icon"
            />
            <span className="ml-2 text-error text-xs font-normal">{error}</span>
          </div>
        )}

        {type === "withdraw" && !isSupportedChain && (
          <div className="flex items-center mt-2">
            <BaseImage
              src="/assets/transactions/warning.svg"
              width={20}
              height={20}
              alt="warning-icon"
            />
            <span className="ml-1 text-md text-[#d4a041] font-medium">
              Please switch to Ethereum to withdraw.
            </span>
          </div>
        )}

        {/* Horizontal Divider */}
        {exchangeRateDisplay && (
          <div className="h-px bg-[#E5E5E5] mt-4 tablet:mt-10 lg:mt-10 -mx-9 w-[calc(100%+4.5rem)]" />
        )}

        {/* Exchange Rate Display */}
        {exchangeRateDisplay && (
          <div className="mt-4 gap-1 flex flex-row items-center">
            <span className="text-xs font-normal text-[#A1B6BA]">
              Exchange rate:
            </span>
            <span className="text-xs font-regular text-text-primary">
              {exchangeRateDisplay}
            </span>
          </div>
        )}

        {/* Disclaimer */}
        {disclaimer && (
          <div className="mt-1.5">
            <span className="text-[10px] leading-3 font-normal text-[#A1B6BA]">
              {disclaimer}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default KernelVaultInput;
